import { Injectable, computed, signal, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, startWith, map, debounceTime } from 'rxjs';

/**
 * Screen size categories for ERP layout.
 *
 * - mobile:  < 992px  — bottom-sheet sidebar, simplified views
 * - compact: 992–1399px — full desktop with auto-collapsed sidebar, horizontal scroll for wide tables
 * - full:    ≥ 1400px — full desktop with expanded sidebar
 */
export enum ScreenSize {
  Mobile = 'mobile',
  Compact = 'compact',
  Full = 'full',
}

/** Breakpoint thresholds (px). Keep in sync with tailwind.css breakpoints. */
export const BREAKPOINTS = {
  /** Below this → mobile */
  mobile: 992,
  /** Below this → compact; at or above → full */
  compact: 1400,
} as const;

@Injectable({
  providedIn: 'root',
})
export class ScreenSizeService {
  private readonly destroyRef = inject(DestroyRef);

  /** Current viewport width in pixels (reactive). */
  readonly width = signal(typeof window !== 'undefined' ? window.innerWidth : 1920);

  /** Current screen size category (reactive). */
  readonly size = computed<ScreenSize>(() => {
    const w = this.width();
    if (w < BREAKPOINTS.mobile) return ScreenSize.Mobile;
    if (w < BREAKPOINTS.compact) return ScreenSize.Compact;
    return ScreenSize.Full;
  });

  /** True when viewport is below mobile breakpoint (< 992px). */
  readonly isMobile = computed(() => this.size() === ScreenSize.Mobile);

  /** True when viewport is in compact range (992–1399px). */
  readonly isCompact = computed(() => this.size() === ScreenSize.Compact);

  /** True when viewport is at full desktop width (≥ 1400px). */
  readonly isFull = computed(() => this.size() === ScreenSize.Full);

  /** True for any desktop mode (compact or full, ≥ 992px). */
  readonly isDesktop = computed(() => !this.isMobile());

  constructor() {
    if (typeof window === 'undefined') return;

    fromEvent(window, 'resize')
      .pipe(
        debounceTime(100),
        map(() => window.innerWidth),
        startWith(window.innerWidth),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((w) => this.width.set(w));
  }
}
